import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import { useAuthentication } from '../hooks/useAuthentication'
import { GET_MY_BUDGET_ACCESS } from '../api/budget'
import { getMainNavItems, getAdminNavItems, NavItem } from './navConfig'

export const DRAWER_WIDTH = 264

export const GET_NOTIFICATION_COUNT = gql`
  query getNotificationCount {
    notificationCount
  }
`

const NavList: React.FC<{ items: NavItem[]; onNavigate?: () => void }> = ({
  items,
  onNavigate,
}) => {
  const location = useLocation()
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {items.map((item) => {
        const Icon = item.icon
        // '/panel' is a prefix of every other panel route, so it can only
        // ever match exactly -- otherwise Dashboard would stay highlighted
        // on every other page.
        const active =
          item.to === '/panel'
            ? location.pathname === '/panel'
            : location.pathname === item.to ||
              location.pathname.startsWith(item.to + '/')
        return (
          <Box
            key={item.to}
            component={Link}
            to={item.to}
            onClick={onNavigate}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1.15,
              borderRadius: 1.5,
              textDecoration: 'none',
              color: active ? '#fff' : 'rgba(255,255,255,0.88)',
              bgcolor: active ? 'rgba(255,255,255,0.14)' : 'transparent',
              fontWeight: active ? 600 : 500,
              fontSize: '0.98rem',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' },
            }}
          >
            <Icon />
            {item.label}
            {!!item.badge && (
              <Box
                sx={{
                  ml: 'auto',
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  borderRadius: 999,
                  px: 1,
                  py: 0.55,
                }}
              >
                {item.badge}
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

type SidebarProps = {
  mobileOpen: boolean
  onMobileClose: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const { authState } = useAuthentication()

  const budgetAccessResult = useQuery(GET_MY_BUDGET_ACCESS, {
    context: { headers: { authorization: authState.authToken } },
    skip: authState.authUser.branch !== 'NATIONAL',
  })
  const budgetAccessLevel = budgetAccessResult.data?.myBudgetAccess?.level

  // The Sidebar is a persistent part of the shell (unlike the old Header,
  // which re-ran this query on every render), so a one-off fetch on mount
  // would go stale as the user works through the inbox without ever
  // reloading. Poll instead of relying on a click to refetch.
  const notificationCountResult = useQuery(GET_NOTIFICATION_COUNT, {
    context: { headers: { authorization: authState.authToken } },
    fetchPolicy: 'network-only',
    pollInterval: 30000,
  })
  const inboxCount = notificationCountResult.data?.notificationCount ?? 0

  const mainItems = getMainNavItems(
    authState.authUser,
    budgetAccessLevel,
    inboxCount,
  )
  const adminItems = getAdminNavItems(authState.authUser)

  const content = (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        height: '100%',
        bgcolor: 'primary.dark',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1,
          py: 1,
          mb: 1,
        }}
      >
        <Avatar alt="logo" src="/logo.svg" sx={{ width: 38, height: 38 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
            VHS
          </Typography>
          <Typography
            noWrap
            sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.7)' }}
          >
            Voucher Handling System
          </Typography>
        </Box>
      </Box>

      <NavList items={mainItems} onNavigate={onMobileClose} />

      {adminItems.length > 0 && (
        <>
          <Typography
            sx={{
              fontSize: '0.78rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '.07em',
              color: 'rgba(255,255,255,0.6)',
              px: 1.5,
              pt: 2.5,
              pb: 0.75,
            }}
          >
            Admin
          </Typography>
          <NavList items={adminItems} onNavigate={onMobileClose} />
        </>
      )}

      <Box
        sx={{
          mt: 'auto',
          pt: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1,
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: 'rgba(255,255,255,0.14)',
            fontSize: '0.85rem',
          }}
        >
          {(authState.authUser.first_name?.[0] || '') +
            (authState.authUser.last_name?.[0] || '')}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            {authState.authUser.first_name} {authState.authUser.last_name}
          </Typography>
          <Typography
            noWrap
            sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)' }}
          >
            {authState.authUser.user_role?.name || ''}
          </Typography>
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, border: 'none' },
        }}
      >
        {content}
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            border: 'none',
            position: 'fixed',
          },
        }}
      >
        {content}
      </Drawer>
    </Box>
  )
}

export default Sidebar
